import { css, SerializedStyles } from '@emotion/core'

import { Color } from '../../../enums/color'
import { isSafari } from '../../../utils/isSafari'
import { ButtonColor, Variant } from '../types'
import {
  filledActiveBg,
  filledText,
  onColorActiveBg,
  onColorText,
  subtleActiveText,
  tonalActiveBg,
  tonalText,
} from './colorTokens'

const getFocusExceptionsStyles = () => {
  if (!isSafari()) {
    return ''
  }

  return css`
    // It removes focus effect in some weird scenarios like during switching items in dropdowns by arrows in Safari browser.
    &:focus-visible {
      outline: none;
    }
  `
}

export const getActiveStyle = (
  variant: Variant,
  forced?: boolean,
  color: ButtonColor = 'primary',
): SerializedStyles => {
  const selectors = ['&:active', ...(forced ? ['&', '&:hover'] : '')].join(',')
  const focusExceptions = getFocusExceptionsStyles()

  return {
    filled: css`
      ${focusExceptions};

      &:not(:disabled) {
        ${selectors} {
          background-color: ${filledActiveBg[color]};
          color: ${filledText[color]};
        }
      }
    `,
    tonal: css`
      ${focusExceptions};

      &:not(:disabled) {
        ${selectors} {
          background-color: ${tonalActiveBg[color]};
          color: ${tonalText[color]};
        }
      }
    `,
    outlined: css`
      ${focusExceptions};

      &:not(:disabled) {
        ${selectors} {
          background-color: ${Color.Grey200};
          color: ${subtleActiveText[color]};
          box-shadow: inset 0 0 0 1px ${Color.Grey200};
        }
      }
    `,
    text: css`
      ${focusExceptions};

      &:not(:disabled) {
        ${selectors} {
          background-color: ${Color.Grey150};
          color: ${subtleActiveText[color]};
        }
      }
    `,
    onColor: css`
      ${focusExceptions};

      &:not(:disabled) {
        ${selectors} {
          background-color: ${onColorActiveBg[color]};
          color: ${onColorText[color]};
        }
      }
    `,
    textOnColor:
      // Accent color is excluded for textOnColor; mirror the plain text active style.
      color === 'accent'
        ? css`
            ${focusExceptions};

            &:not(:disabled) {
              ${selectors} {
                background-color: ${Color.Grey150};
                color: ${subtleActiveText[color]};
              }
            }
          `
        : css`
            ${focusExceptions};

            &:not(:disabled) {
              ${selectors} {
                background-color: rgba(255, 255, 255, 0.2);
                color: ${Color.White};
              }
            }
          `,
  }[variant]
}
